import {
  type MutableRefObject,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames/bind';
import { createPortal } from 'react-dom';
import { useBreakpoint } from '../Breakpoint/BreakpointProvider';
import datepickerStyles from '../DatePicker/DatePicker.module.scss';
import dropdownMLP1Styles from '../DropDown/mlp1/DropDownMLP1.module.scss';
import dropdownMLP2Styles from '../DropDown/mlp2/DropDownMLP2.module.scss';
import { getContainerPortalId, isElementEmpty } from '../utils';
import { DEFAULT_PORTAL_CONTAINER_ID } from '../types/enums';
import { useClickOutsideDetector } from '../hooks';
import styles from './Modal.module.scss';
import { TextAlign } from './types/enums';
import { type RawModalPropsTypes } from './types/propTypes';

const cx = classNames.bind(styles);

function processElementStyle(
  modalRef: MutableRefObject<HTMLDivElement>,
  elementClassName: string,
  subElementClassName: string,
  isDatePicker?: boolean,
) {
  const elementArr = Array.from(
    modalRef.current.getElementsByClassName(elementClassName) as HTMLCollectionOf<HTMLElement>,
  );
  const subElementArr = Array.from(
    modalRef.current.getElementsByClassName(subElementClassName) as HTMLCollectionOf<HTMLElement>,
  );
  if (subElementArr?.length) {
    elementArr.forEach((element, index) => {
      if (subElementArr?.[index]) {
        const boundingElement = element.getBoundingClientRect();
        if (isDatePicker) {
          subElementArr[index].style['left'] = `${
            boundingElement.left - modalRef.current.getBoundingClientRect().left
          }px`;
          subElementArr[index].style['margin'] = '0';
        } else {
          subElementArr[index].style['left'] = `unset`;
        }
        subElementArr[index].style['top'] = `${
          boundingElement.top - modalRef.current.getBoundingClientRect().top + boundingElement.height + 8
        }px`;
        subElementArr[index].style['right'] = `unset`;
        subElementArr[index].style['position'] = 'fixed';
        subElementArr[index].style['width'] = `${boundingElement.width}px`;
      }
    });
  }
}

const RawModal = forwardRef<HTMLDivElement, RawModalPropsTypes>(
  (
    {
      header,
      children,
      containerWidth,
      textAlign,
      closeOnBackdropClick = true,
      onClose,
      id,
      footer,
      className,
      footerClassname,
      useReactPortal = false,
      portalContainerId = DEFAULT_PORTAL_CONTAINER_ID,
    },
    ref,
  ) => {
    const [hasScrollBar, setHasScrollBar] = useState(false);
    const modalRef = useRef() as MutableRefObject<HTMLDivElement>;
    const containerRef = useRef() as MutableRefObject<HTMLDivElement>;
    const breakPoint = useBreakpoint();
    useImperativeHandle(ref, () => modalRef.current);

    const onCloseCB = (() => (closeOnBackdropClick ? onClose : undefined))();
    useClickOutsideDetector(modalRef, onCloseCB, [closeOnBackdropClick], containerRef);

    /**
     * @function `updateState` compares height of modal content and its scroll
     * to show box shadow in case Scroll is present in modal content.
     */
    function updateState() {
      const el = document.getElementById(`nextgen-modal-content-${id}`) as HTMLElement;
      if (el) {
        setHasScrollBar(el.scrollHeight > Math.ceil(el.getBoundingClientRect().height));
      }
    }

    useEffect(() => {
      updateState();
      window.addEventListener('resize', updateState);
      return () => window.removeEventListener('resize', updateState);
    }, [children]);

    //Dropdown Behavior changes
    useLayoutEffect(() => {
      processElementStyle(
        modalRef,
        dropdownMLP2Styles['mlp-2-dropdown-modal'],
        dropdownMLP2Styles['dropdown-drawer'],
        false,
      );
      processElementStyle(
        modalRef,
        dropdownMLP1Styles['dropdown-container-modal'],
        dropdownMLP1Styles['base-dropdown-result'],
        false,
      );
      processElementStyle(
        modalRef,
        datepickerStyles['date-picker-container-within-modal'],
        datepickerStyles['menu-container'],
        true,
      );
    }, [breakPoint]);

    //modal bottom className condition check
    const CModalFooterClassname = cx(
      'modal-bottom-without-secondary',
      {
        'bottom-container': hasScrollBar,
        'modal-bottom-no-footer': !footer || isElementEmpty(footer),
      },
      footerClassname,
    );

    const modalContent = (
      <div ref={containerRef} data-testid="modal-container-data-testid">
        <section className={styles['modal-dialog']}>
          <section
            className={`${styles['modal-container']} ${className}`}
            style={containerWidth ? { width: containerWidth } : {}}
            ref={modalRef}
          >
            {header}
            <section
              id={`nextgen-modal-content-${id}`}
              className={styles[`modal-content-${textAlign}`]}
              data-testid="content-test-id"
              data-allow-scroll="true"
            >
              {children}
            </section>
            <section className={CModalFooterClassname}>{footer}</section>
          </section>
        </section>
      </div>
    );

    const portalContainer = getContainerPortalId(portalContainerId);

    return useReactPortal && portalContainer ? createPortal(modalContent, portalContainer) : modalContent;
  },
);
RawModal.displayName = 'RawModal';
export default RawModal;
export { TextAlign };
